import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ComposedChart, Scatter
} from 'recharts';

const WINDOW_OPTIONS = [
  { label: '1 Week', value: 'week' },
  { label: '1 Month', value: 'month' },
  { label: 'All Time', value: 'all' },
];

// Color palette for charts
const COLORS = {
  primary: '#6366f1',
  secondary: '#8b5cf6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  purple: '#a855f7',
  pink: '#ec4899',
  indigo: '#6366f1',
  blue: '#3b82f6',
  green: '#10b981',
  orange: '#f97316',
  red: '#ef4444',
  gray: '#6b7280'
};

const CHART_COLORS = [COLORS.primary, COLORS.secondary, COLORS.success, COLORS.warning, COLORS.danger, COLORS.info];

function AdminDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [visits, setVisits] = useState(null);
  const [bookmarks, setBookmarks] = useState(null);
  const [users, setUsers] = useState(null);
  const [categoryUsage, setCategoryUsage] = useState(null);
  const [similarBooksUsage, setSimilarBooksUsage] = useState(null);
  const [moodUsage, setMoodUsage] = useState(null);
  const [windowOpt, setWindowOpt] = useState('week');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/admin/check`, { credentials: 'include' });
        if (!res.ok) {
          navigate('/admin/login');
        }
      } catch {
        navigate('/admin/login');
      }
    };
    checkAdmin();
  }, [navigate]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        // Fetch all data in parallel
        const [visitsRes, bookmarksRes, usersRes, categoryRes, similarBooksRes, moodRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/admin/metrics/visits?window=${windowOpt}`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/api/admin/metrics/bookmarks`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/api/admin/metrics/users`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/api/admin/metrics/category-usage?window=${windowOpt}`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/api/admin/metrics/similar-books-usage?window=${windowOpt}`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/api/admin/metrics/mood-usage?window=${windowOpt}`, { credentials: 'include' })
        ]);

        const [visitsData, bookmarksData, usersData, categoryData, similarBooksData, moodData] = await Promise.all([
          visitsRes.json(),
          bookmarksRes.json(),
          usersRes.json(),
          categoryRes.json(),
          similarBooksRes.json(),
          moodRes.json()
        ]);

        setVisits(visitsData);
        setBookmarks(bookmarksData);
        setUsers(usersData);
        setCategoryUsage(categoryData);
        setSimilarBooksUsage(similarBooksData);
        setMoodUsage(moodData);
      } catch (err) {
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [windowOpt]);

  // Format data for charts
  const formatVisitsData = (data) => {
    return data?.daily?.map(item => ({
      date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      visits: item.count
    })) || [];
  };

  const formatCategoryData = (data) => {
    return data?.categories?.slice(0, 5).map((cat, index) => ({
      name: cat.category,
      clicks: cat.count,
      fill: CHART_COLORS[index % CHART_COLORS.length]
    })) || [];
  };

  const formatMoodData = (data) => {
    return data?.moods?.slice(0, 6).map((mood, index) => ({
      name: mood.mood,
      usage: mood.count,
      fill: CHART_COLORS[index % CHART_COLORS.length]
    })) || [];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600 mt-1">Monitor your application's performance and user engagement</p>
            </div>
            <div className="flex gap-2">
              {WINDOW_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setWindowOpt(opt.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    windowOpt === opt.value
                      ? 'bg-indigo-600 text-white shadow-md'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-blue-100">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Site Visits</p>
                <p className="text-2xl font-bold text-gray-900">{visits?.total || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-purple-100">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Bookmarks</p>
                <p className="text-2xl font-bold text-gray-900">{bookmarks?.totalBookmarks || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-green-100">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Users</p>
                <p className="text-2xl font-bold text-gray-900">{users?.totalUsers || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-orange-100">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Similar Books</p>
                <p className="text-2xl font-bold text-gray-900">{similarBooksUsage?.total || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Site Visits Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Site Visits Trend</h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={formatVisitsData(visits)}>
                <defs>
                  <linearGradient id="visitsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="visits" 
                  stroke={COLORS.primary} 
                  fill="url(#visitsGradient)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Category Usage Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Fresh Finds Usage</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={formatCategoryData(categoryUsage)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Bar dataKey="clicks" fill={COLORS.secondary} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Mood Usage Pie Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Mood Usage Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={formatMoodData(moodUsage)}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="usage"
                >
                  {formatMoodData(moodUsage).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Similar Books vs Mood Usage */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Feature Usage Comparison</h3>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={[
                {
                  name: 'Similar Books',
                  usage: similarBooksUsage?.total || 0,
                  color: COLORS.indigo
                },
                {
                  name: 'Mood Usage',
                  usage: moodUsage?.totalMoodUsage || 0,
                  color: COLORS.pink
                }
              ]}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Bar dataKey="usage" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Additional Stats */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h4 className="text-sm font-medium text-gray-600 mb-2">User Engagement</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Users with bookmarks</span>
                <span className="font-semibold">{bookmarks?.uniqueUsersWithBookmarks || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Active subscriptions</span>
                <span className="font-semibold">{users?.activeSubscriptions || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Avg books per user</span>
                <span className="font-semibold">
                  {bookmarks?.uniqueUsersWithBookmarks > 0 
                    ? (bookmarks.totalBookmarks / bookmarks.uniqueUsersWithBookmarks).toFixed(1)
                    : '0'
                  }
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h4 className="text-sm font-medium text-gray-600 mb-2">Top Categories</h4>
            <div className="space-y-2">
              {categoryUsage?.categories?.slice(0, 3).map((cat, index) => (
                <div key={cat.category} className="flex justify-between items-center">
                  <span className="text-gray-600 truncate">{cat.category}</span>
                  <span className="font-semibold">{cat.count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h4 className="text-sm font-medium text-gray-600 mb-2">Top Moods</h4>
            <div className="space-y-2">
              {moodUsage?.moods?.slice(0, 3).map((mood, index) => (
                <div key={mood.mood} className="flex justify-between items-center">
                  <span className="text-gray-600 truncate">{mood.mood}</span>
                  <span className="font-semibold">{mood.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboardPage; 